/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { auth } from '../../firebase.js';

export default function SyncOrderByNumber() {
  const [orderNumber, setOrderNumber] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSync = async () => {
    const trimmed = orderNumber.trim();
    if (!trimmed) {
      setResult({ type: 'error', message: 'Enter an order number first.' });
      return;
    }

    setIsSyncing(true);
    setResult(null);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not signed in');
      }
      const token = await currentUser.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/sync-order-by-number`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderNumber: trimmed })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      setResult({
        type: 'success',
        message: `Synced order #${data.order.orderNumber} — total: $${data.order.totalPrice}, status: ${data.order.financialStatus}, ${data.order.lineItemCount} line item(s).`
      });
      setOrderNumber('');
    } catch (error) {
      setResult({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to sync order'
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSyncing) {
      handleSync();
    }
  };

  return (
    <div className="sync-order-by-number">
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="text"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Order number, e.g. 1172"
          disabled={isSyncing}
          style={{
            padding: '6px 10px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '14px',
            flex: 1,
          }}
        />
        <button
          onClick={handleSync}
          disabled={isSyncing}
          style={{
            padding: '6px 14px',
            backgroundColor: isSyncing ? '#adb5bd' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isSyncing ? 'default' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}
        >
          {isSyncing ? 'Syncing...' : 'Sync Order'}
        </button>
      </div>
      {result && (
        <div
          style={{
            marginTop: '8px',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            backgroundColor: result.type === 'success' ? '#d4edda' : '#f8d7da',
            color: result.type === 'success' ? '#155724' : '#721c24',
          }}
        >
          {result.message}
        </div>
      )}
    </div>
  );
}
